import React, { useEffect, useRef, useState } from 'react';

/**
 * MailCampaignAdmin — bulk-invitation mailer for ky_thuat + ban_su_kien.
 *
 * Workflow (mirrors tools/send-mail-isp/app.py):
 *   1. Upload a CSV/xlsx of recipients. Columns must include `name` and
 *      `email`; `image` and `title` are conventional but optional. Any other
 *      column becomes a {placeholder} usable inside the HTML template.
 *   2. (Optional) Upload one or more inline images. Each recipient row's
 *      `image` cell selects which image to embed (matched by exact filename
 *      or by basename). The image is referenced from the template via the
 *      `cid:{image_cid}` placeholder.
 *   3. Tweak the subject, sender info, delay between emails, extra global
 *      placeholders (event_name, event_time, …), and the HTML template.
 *   4. Hit "Bắt đầu gửi" — the server dispatches sequentially and the panel
 *      polls /status/:jobId every second to live-stream the log.
 *
 * Requires SMTP to be configured (PUT /api/admin/smtp). Without that the
 * server returns 500 immediately when starting a job.
 */

const DEFAULT_TEMPLATE = `<!DOCTYPE html>
<html lang="vi">
<head>
  <meta charset="UTF-8">
  <title>{event_name}</title>
</head>
<body style="font-family:'Times New Roman',Times,serif;font-size:14px;color:#000;background:#f8f8ff;margin:0;padding:20px;">
  <div style="max-width:760px;margin:0 auto;background:#fff;padding:30px;border-radius:8px;box-shadow:0 4px 12px rgba(0,0,0,0.08);">
    <h2 style="color:#4b0082;text-align:center;margin:0 0 16px;">{event_name}</h2>
    <p>Kính gửi <strong>{recipient_title} {recipient_name}</strong>,</p>
    <p>ISP Club xin trân trọng kính mời {recipient_title} tham dự <strong>{event_name}</strong>.</p>
    <div style="background:#f0e6ff;padding:16px;border-left:4px solid #9370db;border-radius:6px;margin:18px 0;">
      <div><strong>🗓️ Thời gian:</strong> {event_time}</div>
      <div><strong>📍 Địa điểm:</strong> {event_location}</div>
    </div>
    <p style="text-align:center;"><img src="cid:{image_cid}" alt="Invitation" style="max-width:480px;width:90%;border-radius:6px;" /></p>
    <p>Trân trọng,<br/><strong>ISP CLUB - CLB An toàn thông tin PTIT</strong></p>
    <hr/>
    <p style="font-size:12px;color:#666;">📞 {club_phone} · 📧 {club_email} · 🌐 {club_facebook}</p>
  </div>
</body>
</html>`;

const DEFAULT_EXTRAS = [
  { key: 'event_name', value: '' },
  { key: 'event_time', value: '' },
  { key: 'event_location', value: '' },
  { key: 'club_phone', value: '' },
  { key: 'club_email', value: '' },
  { key: 'club_facebook', value: '' },
  { key: 'club_address', value: '' },
];

const MailCampaignAdmin = ({ token, lang = {} }) => {
  const authHeaders = { Authorization: `Bearer ${token}` };
  const [columns, setColumns] = useState([]);
  const [recipients, setRecipients] = useState([]);
  const [csvFileName, setCsvFileName] = useState('');
  const [jobImagesId, setJobImagesId] = useState('');
  const [uploadedImages, setUploadedImages] = useState([]);
  const [subject, setSubject] = useState('Thư mời từ ISP CLUB');
  const [senderName, setSenderName] = useState('');
  const [senderEmail, setSenderEmail] = useState('');
  const [delay, setDelay] = useState(3);
  const [template, setTemplate] = useState(DEFAULT_TEMPLATE);
  const [extras, setExtras] = useState(DEFAULT_EXTRAS);
  const [msg, setMsg] = useState(null);
  const [busy, setBusy] = useState(false);
  const [job, setJob] = useState(null); // live status
  const csvInputRef = useRef(null);
  const imagesInputRef = useRef(null);
  const pollTimer = useRef(null);
  const logBoxRef = useRef(null);

  const show = (type, text) => { setMsg({ type, text }); setTimeout(() => setMsg(null), 4500); };

  const [testEmail, setTestEmail] = useState('');
  const [testSending, setTestSending] = useState(false);

  const sendTestEmail = async () => {
    if (!testEmail.trim()) { show('error', 'Vui lòng nhập email test.'); return; }
    if (!subject.trim() || !template.trim()) { show('error', 'Subject và template không được để trống.'); return; }
    setTestSending(true);
    const extraFields = {};
    for (const { key, value } of extras) {
      const k = (key || '').trim();
      if (k) extraFields[k] = value;
    }
    try {
      const res = await fetch('/api/mail-campaign/test-send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          testEmail: testEmail.trim(),
          subject,
          template,
          senderName: senderName || undefined,
          senderEmail: senderEmail || undefined,
          extraFields,
          jobId: jobImagesId || undefined,
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || 'Gửi test thất bại.');
      show('success', data.message || 'Đã gửi test email.');
    } catch (err) { show('error', err.message); }
    finally { setTestSending(false); }
  };

  // Auto-scroll log to bottom on new entries.
  useEffect(() => {
    if (logBoxRef.current) logBoxRef.current.scrollTop = logBoxRef.current.scrollHeight;
  }, [job?.log?.length]);

  // Cleanup polling timer on unmount.
  useEffect(() => () => { if (pollTimer.current) clearInterval(pollTimer.current); }, []);

  const handleCsvUpload = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const res = await fetch('/api/mail-campaign/preview', {
        method: 'POST',
        headers: authHeaders,
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || lang.MAIL_CAMPAIGN_PREVIEW_ERR || 'Lỗi đọc file.');
      setCsvFileName(file.name);
      setColumns(data.columns || []);
      setRecipients(data.recipients || []);
      show('success', (lang.MAIL_CAMPAIGN_PREVIEW_OK || 'Đã đọc {n} người nhận.').replace('{n}', data.recipients.length));
    } catch (err) { show('error', err.message); }
    finally {
      setBusy(false);
      if (csvInputRef.current) csvInputRef.current.value = '';
    }
  };

  const handleImagesUpload = async (e) => {
    const files = Array.from(e.target.files || []);
    if (files.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      // A mixed selection is fine — server side accepts both image files
      // and .zip archives in the same request. We just push every file
      // under a predictable field prefix; the server uses multer.any().
      for (const f of files) {
        const isZip = /\.zip$/i.test(f.name);
        fd.append(isZip ? 'zip' : 'images', f);
      }
      const res = await fetch('/api/mail-campaign/images', {
        method: 'POST',
        headers: authHeaders,
        body: fd
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || lang.MAIL_CAMPAIGN_IMAGES_FAIL || 'Upload ảnh thất bại.');
      setJobImagesId(data.jobId);
      setUploadedImages(data.images || []);
      show('success', (lang.MAIL_CAMPAIGN_IMAGES_UPLOADED || 'Đã upload {n} ảnh.').replace('{n}', data.images.length));
    } catch (err) { show('error', err.message); }
    finally {
      setBusy(false);
      if (imagesInputRef.current) imagesInputRef.current.value = '';
    }
  };

  // Download the sample CSV via an authenticated fetch (the endpoint needs a
  // valid JWT because admin areas are behind authMiddleware). We blob the
  // response and trigger a hidden-anchor download so the file opens cleanly
  // in Excel/Sheets with the UTF-8 BOM intact.
  const downloadSampleCsv = async () => {
    try {
      const res = await fetch('/api/mail-campaign/sample-csv', { headers: authHeaders });
      if (!res.ok) throw new Error('HTTP ' + res.status);
      const blob = await res.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'mail-event-sample.csv';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      setTimeout(() => URL.revokeObjectURL(url), 1000);
    } catch (err) {
      show('error', 'Không tải được file mẫu: ' + err.message);
    }
  };

  const updateExtra = (idx, field, value) => {
    setExtras(prev => prev.map((row, i) => i === idx ? { ...row, [field]: value } : row));
  };
  const addExtra = () => setExtras(prev => [...prev, { key: '', value: '' }]);
  const removeExtra = (idx) => setExtras(prev => prev.filter((_, i) => i !== idx));

  const pollJob = (jobId) => {
    if (pollTimer.current) clearInterval(pollTimer.current);
    pollTimer.current = setInterval(async () => {
      try {
        const res = await fetch(`/api/mail-campaign/status/${jobId}`, { headers: authHeaders });
        const data = await res.json();
        if (!res.ok) throw new Error(data.error);
        setJob(data);
        if (data.done || data.cancelled || !data.running) {
          clearInterval(pollTimer.current);
          pollTimer.current = null;
        }
      } catch (err) {
        clearInterval(pollTimer.current);
        pollTimer.current = null;
        show('error', (lang.MAIL_CAMPAIGN_LOST_CONN || 'Mất kết nối job:') + ' ' + err.message);
      }
    }, 1000);
  };

  const startSend = async () => {
    if (recipients.length === 0) { show('error', lang.MAIL_CAMPAIGN_NO_RECIPIENTS || 'Chưa có danh sách người nhận.'); return; }
    if (!subject.trim() || !template.trim()) { show('error', lang.MAIL_CAMPAIGN_NO_SUBJECT_TPL || 'Subject và template không được để trống.'); return; }
    const confirmMsg = (lang.MAIL_CAMPAIGN_CONFIRM_SEND || 'Gửi mail tới {n} người nhận? Delay giữa các email: {delay}s.')
      .replace('{n}', recipients.length).replace('{delay}', delay);
    if (!window.confirm(confirmMsg)) return;

    const extraFields = {};
    for (const { key, value } of extras) {
      const k = (key || '').trim();
      if (k) extraFields[k] = value;
    }
    setBusy(true);
    try {
      const res = await fetch('/api/mail-campaign/send', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders },
        body: JSON.stringify({
          jobId: jobImagesId || undefined,
          recipients,
          subject,
          senderName: senderName || undefined,
          senderEmail: senderEmail || undefined,
          template,
          delaySeconds: delay,
          extraFields,
          imageCidName: 'image_cid'
        })
      });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error || lang.MAIL_CAMPAIGN_START_ERR || 'Không khởi chạy được job.');
      setJob({ id: data.jobId, total: data.total, sent: 0, failed: 0, running: true, done: false, log: [], failures: [] });
      pollJob(data.jobId);
      show('success', lang.MAIL_CAMPAIGN_START_OK || 'Đã khởi chạy chiến dịch.');
    } catch (err) { show('error', err.message); }
    finally { setBusy(false); }
  };

  const cancelJob = async () => {
    if (!job?.id || !job.running) return;
    if (!window.confirm(lang.MAIL_CAMPAIGN_CANCEL_CONFIRM || 'Hủy chiến dịch? Các email đã gửi không thể thu hồi.')) return;
    try {
      const res = await fetch(`/api/mail-campaign/cancel/${job.id}`, { method: 'POST', headers: authHeaders });
      const data = await res.json();
      if (!res.ok) throw new Error(data.error);
      show('success', lang.MAIL_CAMPAIGN_CANCEL_OK || 'Đã yêu cầu hủy.');
    } catch (err) { show('error', err.message); }
  };

  const progressPct = job && job.total > 0
    ? Math.round(((job.sent + job.failed) / job.total) * 100)
    : 0;

  return (
    <div className="admin-form-card" style={{ display: 'flex', flexDirection: 'column', gap: 24 }}>
      <div>
        <h3 style={{ margin: '0 0 6px' }}>{lang.MAIL_CAMPAIGN_TITLE || 'Gửi Mail Sự Kiện'}</h3>
        <p className="form-hint" style={{ margin: 0 }}>
          {lang.MAIL_CAMPAIGN_DESC || 'Công cụ gửi thư mời sự kiện theo template HTML, có ảnh inline cá nhân hoá theo từng người nhận. Cần cấu hình SMTP trước (tab SMTP Settings – do kỹ thuật quản lý).'}
        </p>
      </div>

      {msg && (
        <div className={`alert alert-${msg.type === 'success' ? 'success' : 'danger'}`} style={{ marginBottom: 0 }}>
          {msg.text}
        </div>
      )}

      {/* === STEP 1: CSV upload === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_STEP1_TITLE || '1. Danh sách người nhận (CSV / xlsx)'}</h4>
        <p className="form-hint" style={{ margin: '0 0 8px' }}>
          {lang.MAIL_CAMPAIGN_STEP1_HINT || 'File phải có ít nhất 2 cột: name, email. Khuyến nghị thêm title (xưng hô: anh/chị/quý CLB...) và image (tên file ảnh inline). Mọi cột khác sẽ trở thành {tên_cột} trong template.'}
          {' '}
          <button
            type="button"
            onClick={downloadSampleCsv}
            style={{
              background: 'none',
              border: 'none',
              padding: 0,
              color: 'var(--accent-teal, #0ea5e9)',
              textDecoration: 'underline',
              cursor: 'pointer',
              font: 'inherit'
            }}
          >
            📥 {lang.MAIL_CAMPAIGN_STEP1_DOWNLOAD_SAMPLE || 'Tải file mẫu (.csv)'}
          </button>
        </p>
        <input
          ref={csvInputRef}
          type="file"
          accept=".csv,.xlsx,.xls"
          onChange={handleCsvUpload}
          disabled={busy}
        />
        {csvFileName && (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            {(lang.MAIL_CAMPAIGN_RECIPIENTS_LOADED || 'Đã đọc {n} người nhận từ {file}.')
              .split('{n}').join(recipients.length).split('{file}').join('')}
            <code>{csvFileName}</code>.
            {' '}{lang.MAIL_CAMPAIGN_COLUMNS || 'Cột phát hiện:'} {columns.map(c => <code key={c} style={{ marginRight: 6 }}>{c}</code>)}
          </div>
        )}
        {recipients.length > 0 && (
          <div style={{ marginTop: 12, maxHeight: 220, overflow: 'auto', border: '1px solid #ddd', borderRadius: 6 }}>
            <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse' }}>
              <thead style={{ background: '#f7f7f7', position: 'sticky', top: 0 }}>
                <tr>
                  <th style={{ padding: 6, textAlign: 'left' }}>#</th>
                  {columns.slice(0, 6).map(c => (
                    <th key={c} style={{ padding: 6, textAlign: 'left' }}>{c}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {recipients.slice(0, 50).map((r, i) => (
                  <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                    <td style={{ padding: 6 }}>{i + 1}</td>
                    {columns.slice(0, 6).map(c => (
                      <td key={c} style={{ padding: 6 }}>{String(r[c] ?? '')}</td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
            {recipients.length > 50 && (
              <div style={{ padding: 6, fontSize: 11, color: '#666', textAlign: 'center' }}>
                {(lang.MAIL_CAMPAIGN_MORE_ROWS || '... và {n} dòng nữa').replace('{n}', recipients.length - 50)}
              </div>
            )}
          </div>
        )}
      </section>

      {/* === STEP 2: Images upload === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_STEP2_TITLE || '2. Ảnh inline (tuỳ chọn)'}</h4>
        <p className="form-hint" style={{ margin: '0 0 8px' }}>
          {lang.MAIL_CAMPAIGN_STEP2_HINT || 'Có thể upload nhiều ảnh cùng lúc HOẶC một file .zip chứa toàn bộ ảnh — server sẽ tự giải nén. Mỗi dòng CSV chọn 1 ảnh qua cột image (khớp tên file). Trong template dùng <img src="cid:{image_cid}" />.'}
        </p>
        <input
          ref={imagesInputRef}
          type="file"
          accept="image/*,.zip,application/zip,application/x-zip-compressed"
          multiple
          onChange={handleImagesUpload}
          disabled={busy}
        />
        {uploadedImages.length > 0 && (
          <div style={{ marginTop: 8, fontSize: 13 }}>
            {(lang.MAIL_CAMPAIGN_IMAGES_LOADED || 'Đã upload {n} ảnh (jobId: {jobId}):')
              .replace('{n}', uploadedImages.length).replace('{jobId}', jobImagesId)}{' '}
            {uploadedImages.slice(0, 8).map(n => <code key={n} style={{ marginRight: 6 }}>{n}</code>)}
            {uploadedImages.length > 8 && <span>...</span>}
          </div>
        )}
      </section>

      {/* === STEP 3: Subject + sender + delay === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_STEP3_TITLE || '3. Thiết lập gửi'}</h4>
        <div className="admin-form-grid">
          <div className="form-group" style={{ gridColumn: 'span 2' }}>
            <label>{lang.MAIL_CAMPAIGN_SUBJECT_LABEL || 'Subject (có thể chứa {placeholder})'}</label>
            <input type="text" value={subject} onChange={e => setSubject(e.target.value)} />
          </div>
          <div className="form-group">
            <label>{lang.MAIL_CAMPAIGN_SENDER_NAME_LABEL || 'Sender Name (mặc định: SMTP fromName)'}</label>
            <input type="text" value={senderName} onChange={e => setSenderName(e.target.value)} placeholder={lang.MAIL_CAMPAIGN_SENDER_NAME_PH || 'ISP Club'} />
          </div>
          <div className="form-group">
            <label>{lang.MAIL_CAMPAIGN_SENDER_EMAIL_LABEL || 'Sender Email (mặc định: SMTP fromEmail)'}</label>
            <input type="email" value={senderEmail} onChange={e => setSenderEmail(e.target.value)} placeholder={lang.MAIL_CAMPAIGN_SENDER_EMAIL_PH || 'ispclubhn@gmail.com'} />
          </div>
          <div className="form-group">
            <label>{lang.MAIL_CAMPAIGN_DELAY_LABEL || 'Delay giữa mỗi email (giây)'}</label>
            <input
              type="number"
              min="0"
              max="60"
              value={delay}
              onChange={e => setDelay(parseInt(e.target.value) || 0)}
            />
          </div>
        </div>
      </section>

      {/* === STEP 4: Extra placeholders === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_STEP4_TITLE || '4. Biến chung (extra placeholders)'}</h4>
        <p className="form-hint" style={{ margin: '0 0 8px' }}>
          {lang.MAIL_CAMPAIGN_STEP4_HINT || 'Áp dụng cho mọi email — ví dụ event_name, event_time... Tham chiếu trong template bằng {tên_biến}.'}
        </p>
        <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
          {extras.map((row, idx) => (
            <div key={idx} style={{ display: 'grid', gridTemplateColumns: '180px 1fr auto', gap: 8 }}>
              <input
                type="text"
                placeholder={lang.MAIL_CAMPAIGN_EXTRA_KEY_PH || 'key (ví dụ: event_name)'}
                value={row.key}
                onChange={e => updateExtra(idx, 'key', e.target.value)}
              />
              <input
                type="text"
                placeholder={lang.MAIL_CAMPAIGN_EXTRA_VALUE_PH || 'value'}
                value={row.value}
                onChange={e => updateExtra(idx, 'value', e.target.value)}
              />
              <button type="button" className="btn btn-outline btn-sm" onClick={() => removeExtra(idx)}>×</button>
            </div>
          ))}
          <button type="button" className="btn btn-outline btn-sm" onClick={addExtra} style={{ alignSelf: 'flex-start' }}>{lang.MAIL_CAMPAIGN_EXTRA_ADD || '+ Thêm biến'}</button>
        </div>
      </section>

      {/* === STEP 5: Template === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_STEP5_TITLE || '5. HTML Template'}</h4>
        <p className="form-hint" style={{ margin: '0 0 8px' }}>
          {lang.MAIL_CAMPAIGN_STEP5_HINT || 'Placeholders sẵn có: {recipient_name}, {recipient_title}, {image_cid} (dùng trong cid:), cộng với mọi cột CSV và biến chung ở mục 4.'}
        </p>
        <textarea
          value={template}
          onChange={e => setTemplate(e.target.value)}
          rows={16}
          style={{ width: '100%', fontFamily: 'monospace', fontSize: 12 }}
        />
      </section>

      {/* === Test email === */}
      <section>
        <h4 style={{ margin: '0 0 8px' }}>📧 Gửi email test</h4>
        <p className="form-hint" style={{ margin: '0 0 8px' }}>
          Nhập email để thử gửi 1 email test theo cấu hình hiện tại (subject, template, biến chung). Subject sẽ được thêm prefix [TEST].
        </p>
        <div style={{ display: 'flex', gap: 8, alignItems: 'center' }}>
          <input
            type="email"
            value={testEmail}
            onChange={e => setTestEmail(e.target.value)}
            placeholder="email-test@example.com"
            style={{ flex: 1, maxWidth: 360 }}
            disabled={testSending}
          />
          <button
            type="button"
            className="btn btn-outline btn-sm"
            onClick={sendTestEmail}
            disabled={testSending || !testEmail.trim() || !subject.trim() || !template.trim()}
          >
            {testSending ? '⏳ Đang gửi...' : '🚀 Gửi test'}
          </button>
        </div>
      </section>

      {/* === STEP 6: Send === */}
      <section style={{ display: 'flex', gap: 12, alignItems: 'center', flexWrap: 'wrap' }}>
        <button
          type="button"
          className="btn btn-primary"
          onClick={startSend}
          disabled={busy || (job && job.running) || recipients.length === 0}
        >
          {lang.MAIL_CAMPAIGN_START || 'Bắt đầu gửi'} {recipients.length > 0 ? `(${recipients.length})` : ''}
        </button>
        {job && job.running && (
          <button type="button" className="btn btn-outline btn-sm" onClick={cancelJob}>
            {lang.MAIL_CAMPAIGN_CANCEL || '⛔ Hủy chiến dịch'}
          </button>
        )}
      </section>

      {/* === STEP 7: Live progress === */}
      {job && (
        <section>
          <h4 style={{ margin: '0 0 8px' }}>{lang.MAIL_CAMPAIGN_PROGRESS_TITLE || '📊 Tiến trình'}</h4>
          <div style={{ display: 'flex', gap: 16, fontSize: 13, marginBottom: 8 }}>
            <span>{lang.MAIL_CAMPAIGN_PROGRESS_TOTAL || 'Tổng:'} <strong>{job.total}</strong></span>
            <span style={{ color: '#16a34a' }}>{lang.MAIL_CAMPAIGN_PROGRESS_SENT || '✅ Thành công:'} <strong>{job.sent}</strong></span>
            <span style={{ color: '#dc2626' }}>{lang.MAIL_CAMPAIGN_PROGRESS_FAILED || '❌ Thất bại:'} <strong>{job.failed}</strong></span>
            <span>{job.done
              ? (job.cancelled ? (lang.MAIL_CAMPAIGN_STATUS_CANCELLED || '⛔ Đã hủy') : (lang.MAIL_CAMPAIGN_STATUS_DONE || '🎉 Hoàn tất'))
              : (job.running ? (lang.MAIL_CAMPAIGN_STATUS_RUNNING || '⏳ Đang chạy') : (lang.MAIL_CAMPAIGN_STATUS_NONE || '—'))}</span>
          </div>
          <div style={{ background: '#eee', height: 10, borderRadius: 6, overflow: 'hidden', marginBottom: 12 }}>
            <div style={{
              width: progressPct + '%',
              height: '100%',
              background: 'linear-gradient(90deg,#9370db,#4b0082)',
              transition: 'width 0.4s ease'
            }} />
          </div>
          <div
            ref={logBoxRef}
            style={{
              background: '#0b0b0e',
              color: '#d4d4d4',
              padding: 12,
              borderRadius: 6,
              fontFamily: 'monospace',
              fontSize: 12,
              maxHeight: 280,
              overflow: 'auto',
              lineHeight: 1.5
            }}
          >
            {(job.log || []).map((entry, i) => (
              <div key={i} style={{
                color: entry.level === 'error' ? '#fca5a5'
                  : entry.level === 'warn' ? '#fcd34d'
                  : entry.level === 'ok' ? '#86efac'
                  : '#d4d4d4'
              }}>
                [{entry.ts.split('T')[1]?.split('.')[0] || ''}] {entry.msg}
              </div>
            ))}
          </div>
          {job.failures && job.failures.length > 0 && (
            <details style={{ marginTop: 8 }}>
              <summary style={{ cursor: 'pointer', color: '#dc2626' }}>
                {(lang.MAIL_CAMPAIGN_FAILURES_SUMMARY || 'Chi tiết {n} email thất bại').replace('{n}', job.failures.length)}
              </summary>
              <table style={{ width: '100%', fontSize: 12, borderCollapse: 'collapse', marginTop: 6 }}>
                <thead><tr><th style={{ padding: 4, textAlign: 'left' }}>{lang.MAIL_CAMPAIGN_TABLE_ROW || 'Row'}</th><th style={{ padding: 4, textAlign: 'left' }}>{lang.MAIL_CAMPAIGN_TABLE_EMAIL || 'Email'}</th><th style={{ padding: 4, textAlign: 'left' }}>{lang.MAIL_CAMPAIGN_TABLE_ERROR || 'Lỗi'}</th></tr></thead>
                <tbody>
                  {job.failures.map((f, i) => (
                    <tr key={i} style={{ borderTop: '1px solid #eee' }}>
                      <td style={{ padding: 4 }}>{f.row}</td>
                      <td style={{ padding: 4 }}>{f.email}</td>
                      <td style={{ padding: 4 }}>{f.error}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </details>
          )}
        </section>
      )}
    </div>
  );
};

export default MailCampaignAdmin;
